import React, { useEffect, useState } from 'react';
import { Trash2, SquarePen } from 'lucide-react';
import { getLeaveRosters, deleteLeaveRoster } from '../../../services/leaveRoasterService';
import Pagination from '../../../components/Pagination/Pagination';
import { formatDate } from '../../../utils/date';

type Status = 'New' | 'Pending Approval' | 'Rejected' | 'Approved';

interface LeaveRoasterRow {
  id: string | number;
  slNo: number;
  refNo: string;
  year: number | string;
  period: string;
  createdDate: string;
  status: Status;
}

interface LeaveRoasterTableProps {
  search?: string;
  refresh?: number;
  onView?: (id: string | number) => void;
  onEdit?: (id: number) => void;
  setMessage?: (message: string) => void;
  setMessageType?: (type: 'success' | 'error' | '') => void;
}

const statusStyles: Record<Status, { text: string; dot: string }> = {
  New: { text: 'text-gray-500', dot: 'bg-gray-400' },
  'Pending Approval': { text: 'text-amber-500', dot: 'bg-amber-500' },
  Rejected: { text: 'text-red-500', dot: 'bg-red-500' },
  Approved: { text: 'text-emerald-500', dot: 'bg-emerald-500' },
};

const MONTH_SHORT = ['JAN', 'FEB', 'MAR', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUG', 'SEPT', 'OCT', 'NOV', 'DEC'];

// start_month / end_month come back as 1-12
const formatPeriod = (startMonth?: number, endMonth?: number) => {
  if (!startMonth || !endMonth) return '—';
  return `${MONTH_SHORT[startMonth - 1]} to ${MONTH_SHORT[endMonth - 1]}`;
};

const formatCreatedDate = (value: string) => {
  if (!value) return '—';
  return formatDate(value, '—');
};

const mapRoster = (roster: any, index: number, pageOffset: number): LeaveRoasterRow => ({
  id: roster.id,
  slNo: pageOffset + index + 1,
  refNo: roster.reference_number ?? `#LR${String(roster.id).padStart(9, '0')}`,
  year: roster.year ?? '—',
  period: formatPeriod(roster.start_month, roster.end_month),
  createdDate: formatCreatedDate(roster.created_at),
  // status comes as { id, title } — title matches our Status labels directly ("New", "Approved", "Rejected", "Pending Approval")
  status: (roster.status?.title as Status) ?? 'New',
});

const columns = ['Sl. No', 'Ref No.', 'Year', 'Start Month & End Month', 'Created Date', 'Status', 'Actions'];

const LeaveRoasterTable: React.FC<LeaveRoasterTableProps> = ({ search = '', refresh = 0, onView, onEdit, setMessage, setMessageType }) => {
  const [rows, setRows] = useState<LeaveRoasterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<string | number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchRosters = async (page: number, searchTerm: string) => {
    setLoading(true);
    try {
      const res = await getLeaveRosters(page, searchTerm);
      const pageOffset = ((res.meta?.current_page ?? page) - 1) * (res.meta?.per_page ?? 10);
      setRows((res.data ?? []).map((roster: any, i: number) => mapRoster(roster, i, pageOffset)));
      setTotalPages(res.meta?.last_page ?? 1);
    } catch (error) {
      console.error('Error fetching leave rosters:', error);
      setMessage?.('Failed to load leave rosters.');
      setMessageType?.('error');
    } finally {
      setLoading(false);
    }
  };

  // re-fetch page 1 whenever the search term (or an external refresh trigger) changes
  useEffect(() => {
    setCurrentPage(1);
    fetchRosters(1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, refresh]);

  useEffect(() => {
    fetchRosters(currentPage, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleDeleteClick = (id: string | number) => {
    setDeleteTarget(id);
  };

  const handleConfirmDelete = async () => {
    if (deleteTarget === null) return;

    try {
      setDeleteLoading(true);
      await deleteLeaveRoster(deleteTarget);
      setMessage?.('Leave roster deleted successfully.');
      setMessageType?.('success');
      fetchRosters(currentPage, search);
    } catch (error) {
      console.error('Error deleting leave roster:', error);
      setMessage?.('Failed to delete leave roster.');
      setMessageType?.('error');
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
      setTimeout(() => {
        setMessage?.('');
        setMessageType?.('');
      }, 3000);
    }
  };

  const handleRowClick = (id: string | number) => {
    onView?.(id);
  };

  return (
    <div className="w-full bg-white p-4">
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col} className="border border-gray-200 px-6 py-4 text-left font-normal text-gray-400">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columns.length} className="border border-gray-200 px-6 py-10 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="border border-gray-200 px-6 py-10 text-center text-gray-400">
                  No leave rosters found.
                </td>
              </tr>
            )}

            {!loading &&
              rows.map((row) => {
                const style = statusStyles[row.status] ?? statusStyles.New;
                const canEdit = row.status !== 'Approved';
                return (
                  <tr key={row.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(row.id)}>
                    <td className="border border-gray-200 px-6 py-4 text-gray-400">{row.slNo}</td>
                    <td className="border border-gray-200 px-6 py-4 font-semibold text-gray-900">{row.refNo}</td>
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">{row.year}</td>
                    <td className="border border-gray-200 px-6 py-4 font-semibold text-gray-900">{row.period}</td>
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">{row.createdDate}</td>
                    <td className="border border-gray-200 px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 font-medium ${style.text}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                        {row.status}
                      </span>
                    </td>
                    <td className="border border-gray-200 px-6 py-4">
                      <div className="flex items-center gap-3 text-gray-400">
                        {canEdit ? (
                          <>
                            <button
                              aria-label="Delete"
                              className="hover:text-red-500"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(row.id);
                              }}
                            >
                              <Trash2 size={16} />
                            </button>
                            <button
                              aria-label="Edit"
                              className="hover:text-blue-500"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit?.(Number(row.id));
                              }}
                            >
                              <SquarePen size={16} />
                            </button>
                          </>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* DELETE CONFIRMATION MODAL */}
      {deleteTarget !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
            <button
              onClick={() => setDeleteTarget(null)}
              disabled={deleteLoading}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              ✕
            </button>

            <h3 className="text-lg font-semibold text-center mb-2">Delete this leave roster?</h3>

            <p className="text-sm text-gray-500 text-center mb-6">This action can&apos;t be undone.</p>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleteLoading}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                No
              </button>

              <button
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveRoasterTable;
